import { apiClient, ApiResponse } from '../api.client';

export interface TripPricing {
  id?: string;
  trip_id?: string;
  currency: string;
  adult_price: number;
  child_price?: number;
  infant_price?: number;
  max_adults?: number;
  max_children?: number;
  max_infants?: number;
  created_at?: string;
  updated_at?: string;
}

export interface TripLocation {
  id?: string;
  trip_id?: string;
  location_name: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  created_at?: string;
  updated_at?: string;
}

export interface TripItinerary {
  id?: string;
  trip_id?: string;
  day_number: number;
  title: string;
  description: string;
  location_name?: string;
  latitude?: number;
  longitude?: number;
  created_at?: string;
  updated_at?: string;
}

export interface Trip {
  id?: string;
  name: string;
  trip_type: 'Join-In' | 'Private';
  stars: number;
  days: number;
  nights: number;
  max_capacity?: number;
  description: string;
  discount_percent?: number;
  is_active: boolean;
  image_url?: string;
  pricing: TripPricing;
  location: TripLocation;
  itineraries?: TripItinerary[];
  inclusions?: string[];
  exclusions?: string[];
  created_at?: string;
  updated_at?: string;
}

export enum AvailabilityStatus {
  AVAILABLE = 'available',
  LIMITED_SPOTS = 'limited_spots',
  FULLY_BOOKED = 'fully_booked',
}

export interface TripAvailability {
  tripId: string;
  tripName: string;
  tripType: 'Join-In' | 'Private';
  date: string;
  maxCapacity: number;
  bookedCount: number;
  availableSpots: number;
  status: AvailabilityStatus;
}

export interface TripsListResponse {
  status: string;
  code: number;
  message: string;
  meta: {
    total_records: number;
    current_page: number;
    page_size: number;
    total_pages: number;
    sort_by: string;
    sort_dir: string;
  };
  data: Trip[];
}

// Fetch all trips with pagination
export async function getTrips(
  page = 1,
  limit = 20,
  sortBy = 'created_at',
  sortDir = 'DESC',
  search = ''
): Promise<TripsListResponse> {
  const params: Record<string, any> = {
    page,
    limit,
    sort_by: sortBy,
    sort_dir: sortDir,
  };

  if (search) {
    params.search = search;
  }

  const response = await apiClient.get<Trip[]>(
    `/api/trips`,
    params
  );
  
  // The backend returns the full response with meta, so we cast it
  return response as unknown as TripsListResponse;
}

// Fetch single trip by ID
export async function getTripById(tripId: string): Promise<ApiResponse<Trip>> {
  const response = await apiClient.get<Trip>(`/api/trips/${tripId}`);
  return response;
}

// Create a new trip
export async function createTrip(trip: Trip): Promise<ApiResponse<Trip>> {
  const response = await apiClient.post<Trip>('/api/trips', trip);
  return response;
}

// Update a trip
export async function updateTrip(tripId: string, trip: Partial<Trip>): Promise<ApiResponse<Trip>> {
  const response = await apiClient.put<Trip>(`/api/trips/${tripId}`, trip);
  return response;
}

// Toggle trip active status
export async function updateTripStatus(tripId: string, is_active: boolean): Promise<ApiResponse<Trip>> {
  const response = await apiClient.patch<Trip>(
    `/api/trips/${tripId}/status`,
    { is_active }
  );
  return response;
}

// Delete a trip
export async function deleteTrip(tripId: string) {
  const response = await apiClient.delete(`/api/trips/${tripId}`);
  return response;
}

// Upload image and get URL
export async function uploadTripImage(base64Data: string, filename: string) {
  const response = await apiClient.post('/api/media/image/base64', {
    base64_data: base64Data,
    filename: filename,
  });
  return response;
}

// Check trip availability for a specific date
export async function checkTripAvailability(
  tripId: string,
  date: string
): Promise<ApiResponse<TripAvailability>> {
  const response = await apiClient.get<TripAvailability>(
    `/api/trips/${tripId}/availability`,
    { date }
  );
  return response;
}
